/**
 * Excel service — writes data to worksheets as Excel Tables (ListObjects)
 * and provides sheet navigation via Office.js APIs.
 */

export interface WriteResult {
  sheetId: string;
  sheetName: string;
  tableName: string;
  rowCount?: number;
  truncated?: boolean;
}

const EXCEL_MAX_ROWS = 1_048_576;
const BATCH_SIZE = 10_000;

// ---- CSV Streaming Import (primary path) ----

/**
 * Import data from a CSV byte stream into a new Excel sheet.
 * Parses CSV incrementally and writes to Excel in batches of BATCH_SIZE rows.
 * This avoids loading the entire dataset into memory at once.
 */
export async function importFromCsvStream(
  displayName: string,
  stream: ReadableStream<Uint8Array>,
  onProgress?: (message: string) => void,
): Promise<WriteResult> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();

  let columns: string[] = [];
  let buffer = ""; // Leftover partial line from previous chunk
  let totalRowCount = 0;
  let sheetId = "";
  let sheetName = "";
  let tableName = "";
  let currentBatch: (string | number)[][] = [];
  let isFirstBatch = true;
  let rowsWrittenSoFar = 0;
  let truncated = false;

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();

      if (value) {
        buffer += decoder.decode(value, { stream: !done });
      }

      // Extract complete CSV records from buffer.
      // A record may span multiple lines if a field contains a quoted newline,
      // so we can't simply split on "\n". Instead, we track whether we're
      // inside a quoted field and only treat newlines outside quotes as
      // record boundaries.
      const records: string[] = [];
      let recordStart = 0;
      let inQuotes = false;

      for (let ci = 0; ci < buffer.length; ci++) {
        const ch = buffer[ci];
        if (ch === '"') {
          inQuotes = !inQuotes;
        } else if (ch === "\n" && !inQuotes) {
          records.push(buffer.substring(recordStart, ci).replace(/\r$/, ""));
          recordStart = ci + 1;
        }
      }

      // Whatever remains is either an incomplete record or trailing data.
      // If the stream is done, treat the remainder as a final record;
      // otherwise keep it in the buffer for the next chunk.
      if (done) {
        const remainder = buffer.substring(recordStart).replace(/\r$/, "");
        if (remainder) records.push(remainder);
        buffer = "";
      } else {
        buffer = buffer.substring(recordStart);
      }

      for (const record of records) {
        if (!record) continue;

        if (columns.length === 0) {
          columns = parseCsvLine(record);
          continue;
        }

        if (totalRowCount >= EXCEL_MAX_ROWS) {
          break;
        }

        let row = parseCsvLine(record);

        // Normalize row length to match header count:
        // pad short rows with empty strings, truncate long rows
        if (row.length < columns.length) {
          row = [...row, ...Array(columns.length - row.length).fill("")];
        } else if (row.length > columns.length) {
          row = row.slice(0, columns.length);
        }

        // Coerce numeric strings to numbers for Excel
        const typedRow = row.map((cell) => {
          if (cell === "" || cell === null) return "";
          const num = Number(cell);
          return !isNaN(num) && cell.trim() !== "" ? num : cell;
        });
        currentBatch.push(typedRow);
        totalRowCount++;

        // Flush batch when it reaches BATCH_SIZE
        if (currentBatch.length >= BATCH_SIZE) {
          if (isFirstBatch) {
            onProgress?.(`Writing rows 1–${totalRowCount.toLocaleString()}…`);
            const result = await writeFirstBatch(
              displayName,
              columns,
              currentBatch,
            );
            sheetId = result.sheetId;
            sheetName = result.sheetName;
            tableName = result.tableName;
            isFirstBatch = false;
          } else {
            onProgress?.(
              `Writing rows ${(totalRowCount - currentBatch.length + 1).toLocaleString()}–${totalRowCount.toLocaleString()}…`,
            );
            await appendRangeToSheet(sheetName, currentBatch, rowsWrittenSoFar + 1, columns.length);
          }
          rowsWrittenSoFar += currentBatch.length;
          currentBatch = [];
        }
      }

      if (done) break;
      if (totalRowCount >= EXCEL_MAX_ROWS) {
        truncated = true;
        break;
      }
    }

    // Flush remaining rows
    if (currentBatch.length > 0) {
      if (isFirstBatch) {
        onProgress?.(`Writing ${totalRowCount.toLocaleString()} rows…`);
        const result = await writeFirstBatch(
          displayName,
          columns,
          currentBatch,
        );
        sheetId = result.sheetId;
        sheetName = result.sheetName;
        tableName = result.tableName;
      } else {
        onProgress?.(
          `Writing final rows (${totalRowCount.toLocaleString()} total)…`,
        );
        await appendRangeToSheet(sheetName, currentBatch, rowsWrittenSoFar + 1, columns.length);
      }
      rowsWrittenSoFar += currentBatch.length;
    }

    // Finalize: resize table over all rows, autofit columns, restore calc
    if (totalRowCount > 0) {
      onProgress?.("Finalizing table…");
      await finalizeTable(tableName, sheetName, totalRowCount, columns.length);
    }
  } finally {
    reader.releaseLock();
  }

  if (columns.length === 0) {
    throw new Error("No data returned from query");
  }

  return { sheetId, sheetName, tableName, rowCount: totalRowCount, truncated };
}

/**
 * Write the first batch: creates the sheet, writes headers + data, creates the Table.
 */
async function writeFirstBatch(
  displayName: string,
  columns: string[],
  rows: (string | number)[][],
): Promise<WriteResult> {
  return Excel.run(async (context) => {
    // Suspend calculation during bulk writes
    context.application.calculationMode = Excel.CalculationMode.manual;

    const sheetName = await getUniqueSheetName(context, displayName);
    const sheet = context.workbook.worksheets.add(sheetName);

    // Headers at row 0
    const headerRange = sheet.getRangeByIndexes(0, 0, 1, columns.length);
    headerRange.values = [columns];

    // Data at rows 1..N
    if (rows.length > 0) {
      const dataRange = sheet.getRangeByIndexes(1, 0, rows.length, columns.length);
      dataRange.values = rows;
    }

    // Create Table over headers + first batch (resized in finalizeTable)
    const totalRows = rows.length + 1;
    const tableRange = sheet.getRangeByIndexes(0, 0, totalRows, columns.length);
    const tableName = `CB_${Date.now()}`;
    const table = sheet.tables.add(tableRange, true);
    table.name = tableName;

    sheet.activate();
    sheet.load("id, name");
    await context.sync();

    return {
      sheetId: sheet.id,
      sheetName: sheet.name,
      tableName,
    };
  });
}

/**
 * Write a batch of rows as a plain range on the sheet (outside the table).
 * Much faster than table.rows.add() because it avoids per-batch table
 * range recalculation. The table is resized once in finalizeTable().
 */
async function appendRangeToSheet(
  sheetName: string,
  rows: (string | number)[][],
  startRow: number,
  columnCount: number,
): Promise<void> {
  const SUB_BATCH = 5_000;
  for (let i = 0; i < rows.length; i += SUB_BATCH) {
    const chunk = rows.slice(i, i + SUB_BATCH);
    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getItem(sheetName);
      const range = sheet.getRangeByIndexes(startRow + i, 0, chunk.length, columnCount);
      range.values = chunk;
      await context.sync();
    });
    // Yield to let Excel UI repaint between sub-batches
    await new Promise((resolve) => setTimeout(resolve, 0));
  }
}

/**
 * Finalize a table after all range writes are complete.
 * Resizes the table to cover all written rows, autofits columns,
 * and restores automatic calculation mode.
 */
async function finalizeTable(
  tableName: string,
  sheetName: string,
  totalDataRows: number,
  columnCount: number,
): Promise<void> {
  await Excel.run(async (context) => {
    const sheet = context.workbook.worksheets.getItem(sheetName);
    const table = context.workbook.tables.getItem(tableName);
    const fullRange = sheet.getRangeByIndexes(0, 0, totalDataRows + 1, columnCount);
    table.resize(fullRange);
    fullRange.format.autofitColumns();
    context.application.calculationMode = Excel.CalculationMode.automatic;
    await context.sync();
  });
}

// ---- CSV Parser ----

/**
 * Parse a single CSV line into an array of field values.
 * Handles quoted fields (RFC 4180): fields containing commas, newlines,
 * or double-quotes are enclosed in double-quotes. Embedded quotes are
 * escaped as "".
 */
function parseCsvLine(line: string): string[] {
  const fields: string[] = [];
  let i = 0;
  const len = line.length;

  while (i <= len) {
    if (i === len) {
      fields.push("");
      break;
    }

    if (line[i] === '"') {
      // Quoted field
      let value = "";
      i++; // skip opening quote
      while (i < len) {
        if (line[i] === '"') {
          if (i + 1 < len && line[i + 1] === '"') {
            // Escaped quote
            value += '"';
            i += 2;
          } else {
            // Closing quote
            i++; // skip closing quote
            break;
          }
        } else {
          value += line[i];
          i++;
        }
      }
      fields.push(value);
      // Skip comma after closing quote
      if (i < len && line[i] === ",") i++;
    } else {
      // Unquoted field
      const commaIdx = line.indexOf(",", i);
      if (commaIdx === -1) {
        fields.push(line.substring(i));
        break;
      } else {
        fields.push(line.substring(i, commaIdx));
        i = commaIdx + 1;
      }
    }
  }

  return fields;
}

// ---- Refresh: replace data in existing table ----

/**
 * Refresh an existing Excel Table by replacing its data from a CSV stream.
 * Clears the table body, parses the new CSV (skipping the header row since
 * the table already has headers), and appends new rows in batches.
 */
export async function refreshTableFromCsvStream(
  tableName: string,
  sheetId: string,
  stream: ReadableStream<Uint8Array>,
  onProgress?: (message: string) => void,
): Promise<WriteResult> {
  // 1. Delete the old table (but keep the sheet)
  onProgress?.("Clearing existing data…");
  try {
    await Excel.run(async (context) => {
      const table = context.workbook.tables.getItem(tableName);
      const tableRange = table.getRange();
      table.delete();
      await context.sync();
      // Clear any leftover cell data from the old table's range
      tableRange.clear();
      await context.sync();
    });
  } catch {
    // Table may already be gone (e.g., previous failed refresh)
  }

  // 2. Parse CSV stream — same logic as importFromCsvStream but writing
  //    to the existing sheet instead of creating a new one
  const reader = stream.getReader();
  const decoder = new TextDecoder();

  let columns: string[] = [];
  let buffer = "";
  let totalRowCount = 0;
  let currentBatch: (string | number)[][] = [];
  let isFirstBatch = true;
  let rowsWrittenSoFar = 0;
  let newTableName = tableName;
  let resolvedSheetName = "";
  let truncated = false;

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (value) {
        buffer += decoder.decode(value, { stream: !done });
      }

      // Quote-aware record splitting
      const records: string[] = [];
      let recordStart = 0;
      let inQuotes = false;

      for (let ci = 0; ci < buffer.length; ci++) {
        const ch = buffer[ci];
        if (ch === '"') {
          inQuotes = !inQuotes;
        } else if (ch === "\n" && !inQuotes) {
          records.push(buffer.substring(recordStart, ci).replace(/\r$/, ""));
          recordStart = ci + 1;
        }
      }

      if (done) {
        const remainder = buffer.substring(recordStart).replace(/\r$/, "");
        if (remainder) records.push(remainder);
        buffer = "";
      } else {
        buffer = buffer.substring(recordStart);
      }

      for (const record of records) {
        if (!record) continue;

        // First record is the header row
        if (columns.length === 0) {
          columns = parseCsvLine(record);
          continue;
        }

        if (totalRowCount >= EXCEL_MAX_ROWS) break;

        let row = parseCsvLine(record);

        // Normalize row length to match column count
        if (row.length < columns.length) {
          row = [...row, ...Array(columns.length - row.length).fill("")];
        } else if (row.length > columns.length) {
          row = row.slice(0, columns.length);
        }

        // Coerce numeric strings to numbers
        const typedRow = row.map((cell) => {
          if (cell === "" || cell === null) return "";
          const num = Number(cell);
          return !isNaN(num) && cell.trim() !== "" ? num : cell;
        });
        currentBatch.push(typedRow);
        totalRowCount++;

        if (currentBatch.length >= BATCH_SIZE) {
          if (isFirstBatch) {
            onProgress?.(`Writing rows 1–${totalRowCount.toLocaleString()}…`);
            const result = await writeRefreshBatch(
              sheetId, tableName, columns, currentBatch,
            );
            newTableName = result.tableName;
            resolvedSheetName = result.sheetName;
            isFirstBatch = false;
          } else {
            onProgress?.(
              `Writing rows ${(totalRowCount - currentBatch.length + 1).toLocaleString()}–${totalRowCount.toLocaleString()}…`,
            );
            await appendRangeToSheet(resolvedSheetName, currentBatch, rowsWrittenSoFar + 1, columns.length);
          }
          rowsWrittenSoFar += currentBatch.length;
          currentBatch = [];
        }
      }

      if (done) break;
      if (totalRowCount >= EXCEL_MAX_ROWS) {
        truncated = true;
        break;
      }
    }

    // Flush remaining rows
    if (currentBatch.length > 0) {
      if (isFirstBatch) {
        onProgress?.(`Writing ${totalRowCount.toLocaleString()} rows…`);
        const result = await writeRefreshBatch(
          sheetId, tableName, columns, currentBatch,
        );
        newTableName = result.tableName;
        resolvedSheetName = result.sheetName;
      } else {
        onProgress?.(
          `Writing final rows (${totalRowCount.toLocaleString()} total)…`,
        );
        await appendRangeToSheet(resolvedSheetName, currentBatch, rowsWrittenSoFar + 1, columns.length);
      }
      rowsWrittenSoFar += currentBatch.length;
    }

    // Finalize: resize table over all rows, autofit columns, restore calc
    if (totalRowCount > 0) {
      onProgress?.("Finalizing table…");
      await finalizeTable(newTableName, resolvedSheetName, totalRowCount, columns.length);
    }
  } finally {
    reader.releaseLock();
  }

  // Use resolved sheet name if available, otherwise look it up
  let sheetName = resolvedSheetName;
  if (!sheetName) {
    try {
      const name = await getSheetNameById(sheetId);
      sheetName = name || "";
    } catch {
      // Fall back to empty
    }
  }

  return { sheetId, sheetName, tableName: newTableName, rowCount: totalRowCount, truncated };
}

/**
 * Write headers + first batch of data to an existing sheet during refresh.
 * Creates a new Table at A1 with the given columns and rows.
 * Returns the new table name.
 */
async function writeRefreshBatch(
  sheetId: string,
  preferredTableName: string,
  columns: string[],
  rows: (string | number)[][],
): Promise<{ tableName: string; sheetName: string }> {
  return Excel.run(async (context) => {
    // Suspend calculation during bulk writes
    context.application.calculationMode = Excel.CalculationMode.manual;

    // Find the sheet by its durable ID
    const sheets = context.workbook.worksheets;
    sheets.load("items/id, items/name");
    await context.sync();
    const sheet = sheets.items.find((s) => s.id === sheetId);
    if (!sheet) throw new Error("Sheet not found");

    // Write headers at row 0
    const headerRange = sheet.getRangeByIndexes(0, 0, 1, columns.length);
    headerRange.values = [columns];

    // Write data at rows 1..N
    if (rows.length > 0) {
      const dataRange = sheet.getRangeByIndexes(1, 0, rows.length, columns.length);
      dataRange.values = rows;
    }

    // Create Table over headers + first batch (resized in finalizeTable)
    const totalRows = rows.length + 1;
    const tableRange = sheet.getRangeByIndexes(0, 0, totalRows, columns.length);
    const newTableName = preferredTableName || `CB_${Date.now()}`;
    const table = sheet.tables.add(tableRange, true);
    table.name = newTableName;

    await context.sync();

    return { tableName: newTableName, sheetName: sheet.name };
  });
}

// ---- Content Hash & Schema ----

/**
 * Compute a SHA-256 hash of an Excel Table's data body.
 * Used to detect user modifications between import/refresh cycles.
 */
export async function computeTableContentHash(
  tableName: string,
): Promise<string> {
  const data = await Excel.run(async (context) => {
    const table = context.workbook.tables.getItem(tableName);
    const bodyRange = table.getDataBodyRange();
    bodyRange.load("values");
    await context.sync();
    return bodyRange.values;
  });

  const text = JSON.stringify(data);
  const encoder = new TextEncoder();
  const hashBuffer = await crypto.subtle.digest("SHA-256", encoder.encode(text));
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Read the column names from an Excel Table's header row.
 * Used for schema drift detection on refresh.
 */
export async function getTableColumnNames(
  tableName: string,
): Promise<string[]> {
  return Excel.run(async (context) => {
    const table = context.workbook.tables.getItem(tableName);
    const headerRange = table.getHeaderRowRange();
    headerRange.load("values");
    await context.sync();

    // headerRange.values is [[col1, col2, ...]]
    return (headerRange.values[0] || []).map((v: unknown) => String(v));
  });
}

// ---- Legacy: bulk write (kept for small datasets / fallback) ----

/**
 * Create a new worksheet, write column headers + data rows as an Excel Table,
 * auto-fit columns, and activate the new sheet.
 */
export async function writeDataToNewSheet(
  displayName: string,
  columns: string[],
  rows: (string | number | null)[][],
): Promise<WriteResult> {
  return Excel.run(async (context) => {
    const sheetName = await getUniqueSheetName(context, displayName);
    const sheet = context.workbook.worksheets.add(sheetName);

    const headerRange = sheet.getRangeByIndexes(0, 0, 1, columns.length);
    headerRange.values = [columns];

    if (rows.length > 0) {
      const dataRange = sheet.getRangeByIndexes(1, 0, rows.length, columns.length);
      const sanitizedRows = rows.map((row) =>
        row.map((cell) => (cell === null ? "" : cell)),
      );
      dataRange.values = sanitizedRows;
    }

    const totalRows = rows.length + 1;
    const tableRange = sheet.getRangeByIndexes(0, 0, totalRows, columns.length);
    const tableName = `CB_${Date.now()}`;
    const table = sheet.tables.add(tableRange, true);
    table.name = tableName;

    tableRange.format.autofitColumns();
    sheet.activate();
    sheet.load("id, name");
    await context.sync();

    return {
      sheetId: sheet.id,
      sheetName: sheet.name,
      tableName,
    };
  });
}

// ---- Sheet Navigation ----

/**
 * Activate (navigate to) a worksheet by its durable ID.
 */
export async function activateSheetById(sheetId: string): Promise<void> {
  return Excel.run(async (context) => {
    const sheets = context.workbook.worksheets;
    sheets.load("items/id");
    await context.sync();

    const sheet = sheets.items.find((s) => s.id === sheetId);
    if (sheet) {
      sheet.activate();
      await context.sync();
    }
  });
}

/**
 * Resolve the current sheet name from a durable sheet ID.
 * Returns the live name (reflects user renames) or undefined if the sheet
 * was deleted.
 */
export async function getSheetNameById(
  sheetId: string,
): Promise<string | undefined> {
  return Excel.run(async (context) => {
    const sheets = context.workbook.worksheets;
    sheets.load("items/id, items/name");
    await context.sync();

    const sheet = sheets.items.find((s) => s.id === sheetId);
    return sheet?.name;
  });
}

// ---- Helpers ----

/**
 * Find a unique sheet name by checking existing sheets and appending
 * a numeric suffix if needed (e.g., "Revenue", "Revenue 2", "Revenue 3").
 */
async function getUniqueSheetName(
  context: Excel.RequestContext,
  displayName: string,
): Promise<string> {
  const sheets = context.workbook.worksheets;
  sheets.load("items/name");
  await context.sync();

  const existingNames = new Set(
    sheets.items.map((s) => s.name.toLowerCase()),
  );

  const baseName = sanitizeSheetName(displayName);
  if (!existingNames.has(baseName.toLowerCase())) {
    return baseName;
  }

  for (let i = 2; i <= 100; i++) {
    const suffix = ` ${i}`;
    const maxBase = 31 - suffix.length;
    const truncated = baseName.substring(0, maxBase).trim();
    const candidate = `${truncated}${suffix}`;
    if (!existingNames.has(candidate.toLowerCase())) {
      return candidate;
    }
  }

  return sanitizeSheetName(`${baseName} ${Date.now()}`);
}

/**
 * Sanitize a string for use as an Excel sheet name.
 * Removes invalid characters and truncates to 31 chars (Excel limit).
 */
function sanitizeSheetName(name: string): string {
  let sanitized = name.replace(/[\\/?*[\]]/g, "");
  sanitized = sanitized.replace(/^'+|'+$/g, "");
  sanitized = sanitized.trim();
  if (sanitized.length > 31) {
    sanitized = sanitized.substring(0, 31).trim();
  }
  return sanitized || "Sheet";
}
